import { useTranslation } from 'react-i18next';
import { Plus, XSquare } from 'react-bootstrap-icons';
import { useState } from 'react';
import { useNotificationContext } from '../../contexts/NotificationContext';
import { useForm } from 'antd/es/form/Form';
import FormModelComponent from '../../components/model/FormModelComponent';
import AddPriceAlertConfig from '../../components/priceAlertConfiguration/AddPriceAlertConfig';
import ConfirmModelComponent from '../../components/model/ConfirmationModelComponent';

import { referenceDataSyncService } from '../../services/referenceDataSyncService';
import { alertConfigService, CreateAlertConfigDto } from '../../api/services/alertConfigService';
import PriceAlertConfigList from '../../components/priceAlertConfiguration/PriceAlertConfigList';
import { trimValues } from '../../utils/trimValues';
import PrimarySquareBtn from '../../components/buttons/PrimarySquareBtn';
import { useIsOnlineContext } from '../../contexts/IsOnlineContext';
import { PermissionEnum } from '../../enums/PermissionEnum';
import { usePermissions } from '../../hooks/usePermissions';

interface IAlertConfig {
  product: string;
  triggeringPrice: string;
  priceManual?: string;
  dateRange?: string;
  condition: string;
  threshold: string;
  department: string;
  municipality: string;
  enableAlert: boolean;
}

const initialAlertConfig = {
  product: '',
  triggeringPrice: '',
  priceManual: '',
  dateRange: '',
  condition: '',
  threshold: '',
  department: '',
  municipality: '',
  enableAlert: true,
};

const PriceAlertsConfiguration: React.FC = () => {
  const { t } = useTranslation('priceAlertsConfiguration');

  const [addAlertConfigForm] = useForm();

  const [newAlertConfig, setNewAlertConfig] = useState<IAlertConfig>(initialAlertConfig);

  const [isAlertConfigModalOpen, setIsAlertConfigModalOpen] = useState<boolean>(false);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState<boolean>(false);

  const [isEdit] = useState<boolean>(false);
  const { successNotification, errorNotification } = useNotificationContext();
  const [listUpdated, setListUpdated] = useState(0);

  const [isConfirmClicked, setIsConfirmClicked] = useState<boolean>(false);

  const { isOnline } = useIsOnlineContext();
  const { hasPermission } = usePermissions();
  const canViewAlertConfigs = hasPermission(PermissionEnum.VIEW_CONFIG_ALERTS);
  const canConfigureAlerts = hasPermission(PermissionEnum.CONFIG_ALERTS);
  const canAccessAlertConfigs = canViewAlertConfigs || canConfigureAlerts;

  const handleNewPriceAlertConfig = () => {
    if (!canConfigureAlerts) {
      return;
    }

    setIsAlertConfigModalOpen(true);
  };

  const getConfirmation = async () => {
    if (!canConfigureAlerts) {
      return;
    }

    await addAlertConfigForm.validateFields();
    setIsConfirmationOpen(true);
  };

  const handleCancel = () => {
    addAlertConfigForm.resetFields();
    setNewAlertConfig(initialAlertConfig);
    setIsAlertConfigModalOpen(false);
  };

  const closeNewAlertConfigModal = () => {
    addAlertConfigForm.resetFields();
    setNewAlertConfig(initialAlertConfig);
    setIsAlertConfigModalOpen(false);
    setIsConfirmationOpen(false);
  };

  const handleCreateAlert = async () => {
    if (!canConfigureAlerts) {
      return;
    }

    // To disable the confirm button to avoid multiple clicks
    setIsConfirmClicked(true);

    const addAlertConfigFormData = addAlertConfigForm.getFieldsValue();

    const trimmedValues = trimValues(addAlertConfigFormData);
    addAlertConfigForm.setFieldsValue(trimmedValues);
    // await addAlertConfigForm.validateFields();

    // Close the popups to avoid multiple clicks
    setIsConfirmationOpen(false);
    setIsAlertConfigModalOpen(false);

    setNewAlertConfig(addAlertConfigFormData);

    let fromDateRangeTimestamp,
      toDateRangeTimestamp,
      startMonitoringDateRangeTimestamp,
      endMonitoringDateRangeTimestamp;

    if (trimmedValues?.triggeringPrice === 'manual_price') {
      if (
        Array.isArray(trimmedValues?.dateRange) &&
        trimmedValues?.dateRange[0] &&
        trimmedValues?.dateRange[1]
      ) {
        const [startMonitoringDateRangeVal, endMonitoringDateRangeVal] =
          trimmedValues?.dateRange || [];
        startMonitoringDateRangeTimestamp = startMonitoringDateRangeVal?.valueOf();
        endMonitoringDateRangeTimestamp = endMonitoringDateRangeVal?.valueOf();
      }
    } else if (trimmedValues?.triggeringPrice === 'average_price') {
      if (
        Array.isArray(trimmedValues?.dateRange) &&
        trimmedValues?.dateRange[0] &&
        trimmedValues?.dateRange[1]
      ) {
        const [fromDateRangeVal, toDateRangeVal] = trimmedValues?.dateRange || [];
        fromDateRangeTimestamp = fromDateRangeVal?.valueOf();
        toDateRangeTimestamp = toDateRangeVal?.valueOf();
      }

      if (
        Array.isArray(trimmedValues?.monitoringDateRange) &&
        trimmedValues?.monitoringDateRange[0] &&
        trimmedValues?.monitoringDateRange[1]
      ) {
        const [startMonitoringDateRangeVal, endMonitoringDateRangeVal] =
          trimmedValues?.monitoringDateRange || [];
        startMonitoringDateRangeTimestamp = startMonitoringDateRangeVal?.valueOf();
        endMonitoringDateRangeTimestamp = endMonitoringDateRangeVal?.valueOf();
      }
    }

    const selectedLocationId = await referenceDataSyncService.getLocationId(
      trimmedValues?.department,
      trimmedValues?.municipality
    );

    const body: CreateAlertConfigDto = {
      productRefId: trimmedValues?.productRefId,
      productCategoryIds: trimmedValues?.productCategoryIds,
      triggeringCondition: trimmedValues?.triggeringPrice as 'manual_price' | 'average_price',
      fromDateRange: fromDateRangeTimestamp,
      toDateRange: toDateRangeTimestamp,
      startMonitoringDateRange: startMonitoringDateRangeTimestamp,
      endMonitoringDateRange: endMonitoringDateRangeTimestamp,
      numberOfInstance: trimmedValues?.numberOfInstance,
      locationId: selectedLocationId,
      enableAlert: trimmedValues?.enableAlert,
    };

    if (trimmedValues?.triggeringPrice === 'manual_price') {
      body.price = trimmedValues?.price;
      body.condition = trimmedValues?.manualPriceCondition;
      delete body.avgPrice;
      delete body.threshold;
    } else if (trimmedValues?.triggeringPrice === 'average_price') {
      body.avgPrice = trimmedValues?.avgPrice;
      body.condition = trimmedValues?.condition;
      body.threshold = trimmedValues?.threshold;
      delete body.price;
    }

    // setIsConfirmationOpen(false);
    // setIsAlertConfigModalOpen(false);

    if (isEdit) {
      /* try {
        const result = await update(selectedProductId, body);

        if (result) {
          const updatedProductData = {
            ...result,
            ...body,
            updateTime: Date.now(),
          };

          const enrichedProduct = await enrichProductWithBasketAndCategory(updatedProductData);

          setProductData((prevProducts) =>
            prevProducts.map((product) =>
              product.refId === selectedProductId ? enrichedProduct : product
            )
          );

          updateLocalData((prevProducts) =>
            prevProducts.map((product) =>
              product.refId === selectedProductId
                ? { ...product, ...body, updateTime: Date.now() }
                : product
            )
          );

          setNewProduct(initialProduct);
          addProductForm.resetFields();
          successNotification(t('productUpdateSuccess'));
          setIsConfirmationOpen(false);
          setIsProductModalOpen(false);
        }
      } catch (error) {
        console.error('Failed to create product:', error);
        setIsProductModalOpen(false);
        setIsConfirmationOpen(false);
        errorNotification(t('productUpdateFail'));
      } */
    } else {
      try {
        const result = await alertConfigService.createAlertConfig(body);

        if (result) {
          setNewAlertConfig(initialAlertConfig);
          addAlertConfigForm.resetFields();
          successNotification(t('priceAlertCreateSuccess'));
          setIsConfirmationOpen(false);
          setIsAlertConfigModalOpen(false);
          setListUpdated((prev) => prev + 1);
        }
      } catch (error) {
        addAlertConfigForm.resetFields();
        setIsAlertConfigModalOpen(false);
        setIsConfirmationOpen(false);
        errorNotification(t('priceAlertCreateFail'));
      } finally {
        setIsConfirmClicked(false);
      }
    }
  };

  const cancelConfirmation = () => {
    setIsConfirmationOpen(false);
  };

  if (!canAccessAlertConfigs) {
    return null;
  }

  return (
    <>
      <ConfirmModelComponent
        isOpen={isConfirmationOpen}
        onClickedCancel={cancelConfirmation}
        header={isEdit ? t('confirmUpdateTitle') : t('confirmCreateTitle')}
        content={<p>{isEdit ? t('confirmUpdateMessage') : t('confirmCreateMessage')}</p>}
        footerAction={{
          confimation: {
            label: t('confirm'),
            onClick: handleCreateAlert,
            isDisabled: isConfirmClicked,
          },
          cancel: {
            label: t('cancel'),
            onClick: cancelConfirmation,
          },
        }}
        styleClasses="z-100"
      />

      <FormModelComponent
        isOpen={isAlertConfigModalOpen}
        header={isEdit ? t('updatePriceAlertTitle') : t('createNewPriceAlertTitle')}
        content={
          <AddPriceAlertConfig
            form={addAlertConfigForm}
            alertConfig={newAlertConfig}
            handleCreate={handleCreateAlert}
            handleCancel={handleCancel}
            getConfirmation={getConfirmation}
            isModal={true}
            isEdit={isEdit}
          />
        }
        onClickedCancel={closeNewAlertConfigModal}
        footerAction={{
          confimation: {
            label: `${isEdit ? t('updateAlert') : t('createAlert')}`,
            onClick: getConfirmation,
          },
          cancel: {
            label: t('cancel'),
            // label: t('back'),
            onClick: handleCancel,
          },
        }}
        closeIcon={<XSquare className="text-primary-font-red" size={20} />}
        contentStyles="mb-4 px-2 max-h-[80vh] overflow-y-auto space-y-2"
      ></FormModelComponent>

      <div className="mt-4 flex w-full flex-col justify-start gap-4 md:flex-row md:items-center md:justify-between">
        <div className="items-start">
          <h2 className="text-xl font-medium text-headings-font-color">
            {t('priceAlertConfigHeading')}
          </h2>
          <p className="text-base font-medium text-font-color">{t('priceAlertConfigSubHeading')}</p>
        </div>

        {canConfigureAlerts && (
          <div className="flex justify-end">
            <PrimarySquareBtn handleOnClick={handleNewPriceAlertConfig} disabled={!isOnline}>
              <div className="flex">
                <Plus className="h-6 w-6 text-white" />
                <p className="pt-1">{t('newPriceAlert')}</p>
              </div>
            </PrimarySquareBtn>
          </div>
        )}
      </div>

      <div className="my-6 w-full">
        <PriceAlertConfigList listUpdated={listUpdated} />
      </div>
    </>
  );
};

export default PriceAlertsConfiguration;
